import { useState } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Field, Stack, Textarea } from '@leanin/ui';
import { FormModal } from '../modals/FormModal';
import { useErrorMessage } from '../../lib/error-message';
import { ReasonRadioGroup } from './ReasonRadioGroup';
import {
  LEADER_REMOVAL_NOTE_MAX,
  LEADER_REMOVAL_REASONS,
  type LeaderRemovalReason,
} from './leader-removal-reasons';

export type RemovalGroupKind = 'circle' | 'network' | 'space';

// What is being removed, and from where. A post leaves one named group; a
// comment leaves the thread of the group its post is shared to; a message is
// tombstoned in its Circle's chat.
export type RemovalTarget =
  | {
      kind: 'post';
      /** Null when the viewer leads the group only through its parent
       *  Network and so isn't sent the scope's name. */
      groupName: string | null;
      groupKind: RemovalGroupKind;
    }
  | { kind: 'comment'; groupKind: RemovalGroupKind }
  | { kind: 'message' };

export type RemoveContentInput = {
  reason: LeaderRemovalReason;
  /** Omitted when the leader left the note empty. */
  note?: string;
};

// Callers mount it only while open (keyed to the content), so each open
// starts from an empty form.
type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  target: RemovalTarget;
  /** Who the note is for. A member is addressed by first name; an author who
   *  has left can't read it, so the note goes to the moderation team only
   *  (it is filed on the removal's moderation report either way). */
  author: RemovalAuthor;
  /** The caller's removal mutation. Rejects on failure; the dialog shows the
   *  cataloged copy for the error code (e.g. `feed/remove_own_content`). */
  submit: (input: RemoveContentInput) => Promise<void>;
  /** Fires after a successful removal, once the dialog has asked to close. */
  onRemoved?: () => void;
};

export type RemovalAuthor =
  | { readonly kind: 'member'; readonly name: string }
  | { readonly kind: 'deleted' };

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

// A group leader removing a post, comment or message from a group they lead.
// Built from the same FormModal / reason-radio / Textarea pieces as
// ReportContentDialog, but a removal is an action, not a report: no
// confirmation screen, the leader's own reasons, and a note to the author.
export function RemoveContentDialog({
  isOpen,
  onOpenChange,
  target,
  author,
  submit,
  onRemoved,
}: Props) {
  const t = useTranslations('Moderation');
  const describeError = useErrorMessage();
  const [reason, setReason] = useState<LeaderRemovalReason | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const groupKind = target.kind === 'message' ? 'circle' : target.groupKind;

  const onSubmit = async () => {
    if (reason === null) return;
    setError(null);
    setSubmitting(true);
    const trimmed = note.trim();
    try {
      await submit({ reason, ...(trimmed.length > 0 ? { note: trimmed } : {}) });
      onOpenChange(false);
      onRemoved?.();
    } catch (err: unknown) {
      setError(describeError(err, t('leaderRemoval.dialog.error')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onOpenChange={onOpenChange}
      title={
        target.kind === 'post'
          ? target.groupName !== null
            ? t('leaderRemoval.dialog.postTitle', { group: target.groupName })
            : t('leaderRemoval.dialog.postTitleUnnamed', { kind: groupKind })
          : target.kind === 'comment'
            ? t('leaderRemoval.dialog.commentTitle')
            : t('leaderRemoval.dialog.messageTitle')
      }
      primaryLabel={
        target.kind === 'post'
          ? t('leaderRemoval.dialog.removePost', { kind: groupKind })
          : target.kind === 'comment'
            ? t('leaderRemoval.dialog.removeComment')
            : t('leaderRemoval.dialog.removeMessage')
      }
      secondaryLabel={t('leaderRemoval.dialog.cancel')}
      primaryDisabled={reason === null}
      isSubmitting={submitting}
      error={error}
      onSubmit={onSubmit}
    >
      <Stack gap="md">
        <ReasonRadioGroup
          options={LEADER_REMOVAL_REASONS.map((value) => ({
            value,
            label: t(`leaderRemoval.reasons.${value}.label`),
          }))}
          value={reason}
          onChange={setReason}
          ariaLabel={t('leaderRemoval.dialog.reasonAria')}
        />
        <Field
          label={
            author.kind === 'member'
              ? t('leaderRemoval.dialog.noteLabel', { name: firstName(author.name) })
              : t('leaderRemoval.dialog.noteLabelTeam')
          }
          {...(author.kind === 'deleted'
            ? { description: t('leaderRemoval.dialog.noteDescriptionAuthorLeft') }
            : {})}
        >
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            maxLength={LEADER_REMOVAL_NOTE_MAX}
            placeholder={
              author.kind === 'member'
                ? t('leaderRemoval.dialog.notePlaceholder')
                : t('leaderRemoval.dialog.notePlaceholderTeam')
            }
          />
        </Field>
      </Stack>
    </FormModal>
  );
}
